import axios, { AxiosRequestConfig } from 'axios'

import { ENV } from './env'

/* -----------------------------------------------------------------------------
 * http client
 *
 * The professional app talks only to Flipper. This replaces the `useFlipperRequest`
 * hook from @walrus/common, which resolved its token through `useSelector` and so
 * tied every request to redux.
 * -------------------------------------------------------------------------- */

// flipper answers a slashless path with a 307 whose Location downgrades to
// http, and the browser drops the request rather than follow it across
// origins. Paths carry their canonical trailing slash; this keeps the host
// itself on https even if the env ever hands us an http url. localhost is
// left alone so a local api still works.
const toHttps = (url: string) =>
  /^http:\/\/(?!localhost|127\.0\.0\.1)/.test(url)
    ? url.replace(/^http:/, 'https:')
    : url

// every flipper route is served under the /flipper prefix
const baseURL = `${toHttps(ENV.flipper_url)}/flipper`

// the session lives in store/Auth; a provider keeps this module free of
// any import back into the stores that use it
export type SessionProvider = {
  // a token that is not about to expire, refreshed first when it is
  getAccessToken: () => Promise<string>
  // rotates the credentials after flipper rejected the token; false once the
  // session is gone
  refreshSession: () => Promise<boolean>
}

let session: SessionProvider = {
  getAccessToken: async () => '',
  refreshSession: async () => false
}

export const registerSessionProvider = (provider: SessionProvider) => {
  session = provider
}

declare module 'axios' {
  interface AxiosRequestConfig {
    // set on the one retry a request gets after its token was rejected
    sessionRetry?: boolean
  }
}

export const httpClient = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json'
  }
})

httpClient.interceptors.request.use(async config => {
  // an explicit `Authorization: null` opts a call out of the session token,
  // which login, mfa and the refresh itself need so a stale token never
  // rides along
  if (config.headers.Authorization === null) {
    delete config.headers.Authorization
  } else {
    const token = await session.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }

  // FormData carries its own multipart boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
})

// a token can still be rejected before it expires: the device was revoked
// (logout elsewhere, a third device, a password change) or the computer slept
// past the refresh. One refresh tells the two apart: it either hands back a
// token for a single retry, or ends the session.
const retryWithRefreshedSession = async (config?: AxiosRequestConfig) => {
  const sentToken = !!config?.headers?.Authorization
  if (!config || !sentToken || config.sessionRetry) {
    return null
  }

  const refreshed = await session.refreshSession()
  return refreshed ? httpClient({ ...config, sessionRetry: true }) : null
}

httpClient.interceptors.response.use(
  async response =>
    response.status === 401
      ? (await retryWithRefreshedSession(response.config)) ?? response
      : response,
  async error => {
    if (error?.response?.status === 401) {
      const retried = await retryWithRefreshedSession(error.config)
      if (retried) {
        return retried
      }
    }
    return Promise.reject(error)
  }
)

/* -----------------------------------------------------------------------------
 * helpers
 *
 * The stores want the payload, not the envelope. An empty body comes back as
 * null instead of throwing, which is what `res.json()` did on 204s.
 * -------------------------------------------------------------------------- */

const unwrap = <T>(data: T): T =>
  data === ('' as unknown as T) ? (null as unknown as T) : data

export const http = {
  get: <T = any>(path: string, config?: AxiosRequestConfig) =>
    httpClient.get<T>(path, config).then(res => unwrap(res.data)),

  post: <T = any>(path: string, body?: unknown, config?: AxiosRequestConfig) =>
    httpClient.post<T>(path, body, config).then(res => unwrap(res.data)),

  put: <T = any>(path: string, body?: unknown, config?: AxiosRequestConfig) =>
    httpClient.put<T>(path, body, config).then(res => unwrap(res.data)),

  patch: <T = any>(path: string, body?: unknown, config?: AxiosRequestConfig) =>
    httpClient.patch<T>(path, body, config).then(res => unwrap(res.data)),

  delete: <T = any>(path: string, config?: AxiosRequestConfig) =>
    httpClient.delete<T>(path, config).then(res => unwrap(res.data))
}

export default http
